import { IStatus } from "dto/IStatus";
import { ITag, TagType } from "dto/ITag";
import moment from "moment";
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import ThreadIcon from "assets/icons/thread.svg?react";
import ThreadActiveIcon from "assets/icons/thread_active.svg?react";
import Chip from "components/Chip";
import IconsState from "components/IconsState";
import { ESendStatus } from "components/IconsState/typings";
import OptionsCard from "components/OptionsCard/OptionsCard";
import PackageBar from "components/PackageBar";
import { ETypeOfOrder } from "components/SortOrder/typings";
import ThreadList from "components/ThreadList";
import PackagesContext from "contexts/PackagesContext";
import sanitizeHtml from "sanitize-html";
import EventContext from "contexts/EventContext";
import ReplyIcon from "assets/icons/reply_all.svg?react";
import ForwardIcon from "assets/icons/forward.svg?react";
import { axiosInstance } from "api/axios";
import { useThreadStore } from "store/thread.store";
import GroupIcon from "assets/icons/groups.svg?react";
import OutgoingParcelReceiversModal from "components/OutgoingParcelReceiversModal";
import { ItemKind } from "dto/IKindItems";
import { cleanEmailSubject } from "utils/cleanEmailSubject";
import classNames from "classnames";
import Popper from "components/Popper";
import { Tooltip } from "@mui/material";
import StatusModal from "components/StatusModal";
import ChooseTagNew from "components/ChooseTagNew";
import { useInView } from "framer-motion";
import AppContext from "contexts/AppContext";
import PackageEditSharesModal from "components/PackageEditSharesModal";
import MetricDatesModal from "components/MetricDatesModal";
import { useTranslation } from "react-i18next";

const MessageCard: FC<Props> = ({
  onClick,
  msg,
  statuses,
  tags,
  keys,
  isFirst = false,
  isThread = false,
  isSelectable = false,
  selectedElements,
  onDownload,
  setSelectedElements,
  onPreview,
  selectMultiple,
  scrollWrapper,
  isIncoming,
  remove,
  listFilter,
  inDeskView,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    filterId,
    status: linkStatus,
    threadId,
    threadPackageId,
  } = useParams();
  let { packageId } = useParams();
  const { setActivePanel } = useContext(AppContext);

  const isOutcoming = window.location.href.search("outcoming") !== -1;

  packageId = encodeURIComponent(packageId ?? "");

  const [status, setStatus] = useState<IStatus | null>(null);
  const [findedTags, setFindedTags] = useState<ITag[]>([]);
  const refWrapper = useRef(null);
  const [, setShowAttach] = useState(false);

  const [showEditTags, setShowEditTags] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const isSelected = !!selectedElements?.find((s) => s.Id === msg.Id) ?? false;

  // Use object destructuring, so you don't need to remember the exact order
  const inView = useInView(refWrapper, { root: scrollWrapper });

  useEffect(() => {
    if (isSelected && !inView) {
      const timeout = setTimeout(() => {
        console.log("Scroll to element");
        refWrapper.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [isSelected]);

  useEffect(() => {
    const t = tags?.filter((tag) =>
      msg?.TagIds?.find((iTag) => tag.Id === iTag),
    );
    setFindedTags(t);
    const newStatus = statuses?.find((s) => s.Id === msg.StatusId);

    setStatus(newStatus ?? null);
  }, [msg, statuses, msg.StatusId, msg.TagIds]);

  const handleStatusUpdate = (status?: IStatus) => {
    setStatus(status);
    setShowEditStatus(false);
  };

  const { packages, setPackages } = useContext(PackagesContext);
  const { messages: threadMessages, setMessages: setThreadMessages } =
    useThreadStore();
  const [showOptions, setShowOptions] = useState(false);

  const { blockClick } = useContext(EventContext);

  const handleClick = (e) => {
    if (blockClick || e?.target?.className?.search?.("sep") !== -1) return;
    const ele = e.target.tagName.toLowerCase();
    if (ele === "svg") return;

    if (isSelectable && selectedElements && setSelectedElements) {
      if (selectMultiple) {
        const startPosition = selectedElements.length
          ? packages?.indexOf(selectedElements[selectedElements.length - 1])
          : -1;

        const index = packages?.indexOf(msg);

        const endPosition =
          packages?.indexOf(selectedElements[selectedElements.length - 1]) ??
          packages?.indexOf(msg);

        if (startPosition === -1) {
          const elements = packages?.filter(
            (e, i) => i <= index && i >= startPosition,
          );
          setSelectedElements(elements);
          return;
        }

        if (index < startPosition) {
          const elements = packages?.filter(
            (e, i) => i >= index && i <= startPosition,
          );

          const objs = [...selectedElements, elements].flat();

          const uniqueData = objs?.filter(
            (obj, index, self) =>
              index === self?.findIndex((o) => o.Id === obj.Id),
          );

          setSelectedElements(uniqueData);
          return;
        }

        if (index > endPosition) {
          const elements = packages?.filter(
            (e, i) => i <= index && i >= startPosition,
          );

          const objs = [...selectedElements, elements]?.flat();

          const uniqueData = objs?.filter(
            (obj, index, self) =>
              index === self?.findIndex((o) => o.Id === obj.Id),
          );

          setSelectedElements(uniqueData);
        } else {
          const elements = selectedElements?.filter((a, b) => b <= index);
          setSelectedElements(elements);
        }

        return;
      }

      const el = selectedElements?.find((p) => p.Id === msg.Id);
      if (el) {
        setSelectedElements(selectedElements?.filter((p) => p.Id !== msg.Id));
        return;
      }
      setSelectedElements([...selectedElements, msg]);
      return;
    }

    const link = linkStatus
      ? linkStatus
      : isIncoming === true
      ? "incoming"
      : "outcoming";

    if (!isSelected) {
      setSelectedElements?.([msg]);

      if (msg.Kind === 256) {
        navigate(
          `/packages/${filterId}/${link}/${msg.Kind}/${msg.Id}/preview-letter/${msg.Id}`,
        );
        return;
      }

      if (msg?.Thread?.Id && (msg?.Thread?.Count > 1 || isThread)) {
        navigate(
          `/packages/thread/${filterId}/${link}/${msg.Kind}/${msg.Thread.Id}/${msg.Id}`,
        );
        return;
      }

      navigate(`/packages/${filterId}/${link}/${msg.Kind}/${msg.Id}`);
    } else {
      setSelectedElements?.([]);
      navigate(`/packages/${filterId}/${link}`);
    }
  };

  useEffect(() => {
    if (threadId === msg.Thread?.Id && threadPackageId !== msg.Id) {
      setShowThreads(true);
    }
  }, [threadId, threadPackageId]);

  const isWaitingToReceive =
    !msg.ReceiveDate &&
    (msg.Kind === ItemKind.CourtPortalDeliverance ||
      msg.Kind === ItemKind.EDeliveryMessage);

  const threadCounter = msg?.Thread?.Count ?? 0;

  const [showThreads, setShowThreads] = useState(false);

  let senderOrReceiver = msg?.Sender;

  const [showReceivers, setShowReceivers] = useState(false);
  const hasMoreReceivers = msg?.Receivers && msg.Receivers.length > 1;
  const receiversReference = useRef();

  const isCurrentDate = moment(msg?.SendDate ?? msg?.CreationDate).isSame(
    new Date(),
    "day",
  );

  const isTomorrow = moment(msg?.SendDate ?? msg?.CreationDate).isSame(
    moment(new Date()).subtract(1, "day").startOf("day"),
    "day",
  );

  if (isOutcoming || (!msg.IsIncoming && isIncoming)) {
    if (msg.Receivers) {
      const rec = msg?.Receivers?.find((a) => !a.Cc);

      if (rec) {
        senderOrReceiver = rec.Name && rec.Name.length ? rec.Name : rec.Email;
      }
    }
  }

  const handleUpdateTags = (tags: ITag[]) => {
    const newPackages = [...packages];

    const el = newPackages.find((c) => c.Id === msg.Id && c.Kind === msg.Kind);

    if (el) {
      el.TagIds = tags.map((t) => t.Id);
    }

    setPackages(newPackages);
  };

  const time = msg?.SendDate ?? msg?.CreationDate ?? new Date();

  // if (listFilter?.type === ETypeOfOrder.CREATION_DATE) {
  //   time = msg.CreationDate;
  // }

  const isSended = msg?.Receivers?.find(
    (s) =>
      s?.SendStatus === ESendStatus.PROCESSING ||
      s?.SendStatus === ESendStatus.WAITING ||
      s?.SendStatus === ESendStatus.ERROR,
  );

  let data = "";
  if (
    (listFilter?.type === ETypeOfOrder.RECEIVED_DATE && msg.Unread) ||
    (listFilter?.type === ETypeOfOrder.SEND_DATE && isSended)
  ) {
    data = "";
  } else {
    data = moment(time).format("DD.MM.YY").toString();

    if (isCurrentDate) {
      data = `Dzisiaj | ${moment(time).format("HH:mm").toString()}`;
    }

    if (isTomorrow) {
      data = `Wczoraj | ${moment(time).format("HH:mm").toString()}`;
    }
  }

  const dateRef = useRef();

  const [showDateMetric, setShowDateMetic] = useState<boolean>(false);

  useEffect(() => {
    if (!msg) return;
    if (packageId === msg.Id || msg.Id === threadPackageId) {
      setShowOptions(true);
    }

    if (showOptions && packageId !== msg.Id && msg.Id !== threadPackageId) {
      setShowOptions(false);
    }

    const newPacks = [...packages];
    const actual = newPacks?.find((a) => a.Id === packageId);

    if (!actual) return;

    if (
      actual.Kind !== ItemKind.EDeliveryMessage &&
      actual.Kind !== ItemKind.CourtPortalDeliverance
    ) {
      actual.Unread = false;

      setPackages(newPacks);
    }
  }, [packageId, threadPackageId]);

  const handleClickRightButton = () => {
    if (msg.Id === packageId) return;
    setShowOptions(true);
    setIsRightClicked(true);
  };

  useEffect(() => {
    if (
      (msg.Id === packageId || msg.Id === threadPackageId) &&
      msg.Unread &&
      msg.Kind !== ItemKind.EDeliveryMessage &&
      msg.Kind !== ItemKind.CourtPortalDeliverance
    ) {
      axiosInstance
        .post("/list/read", {
          Items: [
            {
              Id: msg.Id,
              Kind: msg.Kind,
            },
          ],
        })
        .then(() => {
          const t = [...packages];
          const tt = msg.Thread?.Id
            ? [...(threadMessages[msg.Thread?.Id] ?? [])]
            : [];
          const e = t.find(
            (a) => a.Id === packageId || a.Id === threadPackageId,
          );
          const e2 = tt.find(
            (a) => a.Id === packageId || a.Id === threadPackageId,
          );
          const threadParent = t.find((a) => a.Thread?.Id === msg.Thread?.Id);

          if (threadParent && threadParent?.Thread?.UnreadCount > 0) {
            threadParent.Thread.UnreadCount--;
          }

          if (e) {
            msg.Unread = false;
            setPackages(t);
          }

          if (e2) {
            msg.Unread = false;
            setThreadMessages(msg.Thread?.Id, tt);
          }
        });
    }
  }, [msg, packageId, threadPackageId]);

  // useEffect(() => {
  //   if (msg.Id === packageId || msg.Id === threadPackageId) {
  //     setSelectedElements([msg]);
  //   }
  // }, []);

  const [isRightClicked, setIsRightClicked] = useState(false);
  const [callClose, setCallClose] = useState(false);

  const [isMouse, setIsMouse] = useState(false);

  const [height, setHeight] = useState(msg.isNew ? "0px" : "initial");
  const contentRef = useRef<HTMLDivElement>(null);
  const [statusPopperRef, setStatusPopperRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);
  const [tagPopperRef, setTagPopperRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);
  const [sharePopperRef, setSharePopperRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);
  const iconsRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef?.current) return;
    if (!msg?.isNew) return;

    // Oblicz wysokość zawartości i ustaw ją w stanie
    setHeight(`${contentRef.current.scrollHeight}px`);
    setTimeout(() => {
      setHeight("initial");
    }, 1000);
  }, [contentRef, msg]);

  const createRipple = (event: MouseEvent) => {
    const button = event.currentTarget as HTMLElement;
    const ripple = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${event.clientX - button.offsetLeft - radius}px`;
    ripple.style.top = `${event.clientY - button.offsetTop - radius}px`;
    ripple.classList.add(styles.ripple);

    const rippleContainer = button.querySelector(`.${styles.rippleContainer}`);
    rippleContainer?.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <div
      ref={contentRef}
      style={{ height: msg?.isNew ? height : null }} // Ustaw wysokość na podstawie stanu
      tabIndex={0}
      onFocus={() => setActivePanel("left")}
      className={`${
        !isThread && msg.Thread && showThreads && styles.borderThread
      } ${styles.msgWrapper} ${isSelected ? styles.selected : null}`}
    >
      <div
        data-id={msg.Id}
        onMouseEnter={() => setIsMouse(true)}
        onMouseLeave={() => setIsMouse(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          handleClickRightButton();
        }}
      >
        <div
          className={`${msg.Thread && styles.threadList} ${
            styles.rippleContainer
          }`}
          onClick={(e: any) => {
            createRipple(e);
          }}
        >
          <div
            ref={refWrapper}
            onClick={
              onClick
                ? (e: any) => {
                    const ele = e.target.tagName.toLowerCase();
                    const button = e.target.outerHTML.search("button") !== -1;

                    if (ele !== "div" || button) {
                      return;
                    }

                    onClick?.();
                  }
                : handleClick
            }
            className={`${styles.card} ${
              msg.Thread && showThreads && styles.thread
            } ${isThread && styles.threadCard} ${
              isSelected && styles.active_selected
            } ${
              !msg.ReceiveDate &&
              isWaitingToReceive &&
              linkStatus === "incoming" &&
              styles.maxHeight
            } ${msg.isGotNow && styles.animation} ${
              msg.Thread && !isThread && styles.mainCard
            }`}
          >
            {msg.Unread && !msg.InChat && (
              <Tooltip title={t("chat.unreadMessage")}>
                <div className={styles.colorBar}></div>
              </Tooltip>
            )}
            {!msg.Unread && !msg.InChat && (
              <>
                {isIncoming && !msg.IsIncoming && !msg.IsForwarded && (
                  <div className={styles.directionIcon}>
                    <ReplyIcon />
                  </div>
                )}
                {((isIncoming && !msg.IsIncoming && msg.IsForwarded) ||
                  msg.Pass) && (
                  <div className={styles.directionIcon}>
                    <ForwardIcon />
                  </div>
                )}
              </>
            )}
            {/* {msg?.Thread?.Count > 1 && msg?.Thread?.UnreadCount > 0 && (
              <div className={styles.colorBar}>
                {msg?.Thread?.UnreadCount ?? 0}
              </div>
            )} */}
            {/* <div
              style={{ backgroundColor: statusColor }}
              className={styles.statusBar}
            ></div> */}
            <div className={`${styles.header}`}>
              <div className={styles.headerWrapper}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(senderOrReceiver ?? ""),
                  }}
                  className={styles.sender}
                ></div>
                {/* {showBadgeOnIncomingMessage && msg.IsIncoming && (
                  <SealIcon></SealIcon>
                )} */}
                {hasMoreReceivers && (
                  <div
                    className={classNames(
                      styles.moreReceivers,
                      msg.SearchHit?.InReceivers ? styles.searchHit : null,
                    )}
                    ref={receiversReference}
                    onClick={(e) => {
                      setShowReceivers(!showReceivers);
                      e.stopPropagation();
                    }}
                  >
                    <GroupIcon></GroupIcon> +{" "}
                    {(msg?.Receivers?.length ?? 0) - 1}
                  </div>
                )}

                <Popper
                  open={showReceivers}
                  anchorEl={receiversReference?.current}
                  setOpen={setShowReceivers}
                >
                  <OutgoingParcelReceiversModal
                    toggle={() => setShowReceivers(false)}
                    pack={msg}
                  />
                </Popper>
              </div>
              <div className={styles.dateWrapper}>
                <Tooltip title={t("packages.showDates")}>
                  <div
                    className={styles.date}
                    ref={dateRef}
                    onClick={(e) => {
                      setShowDateMetic(true);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {data}
                  </div>
                </Tooltip>
                {msg?.Thread && msg.Thread.Count > 1 && !isThread && (
                  <div className={styles.threade}>
                    <div className={styles.threadCounter}>
                      {threadCounter > 0 ? threadCounter : ""}
                    </div>
                    <>
                      <div className={styles.threadIconWrapper}>
                        {showThreads ? (
                          <ThreadActiveIcon
                            className={`${styles.threadIcon} ${
                              showThreads ? styles.threadsActive : ""
                            }`}
                            onClick={() => {
                              setCallClose(true);
                            }}
                          />
                        ) : (
                          <ThreadIcon
                            className={`${styles.threadIcon} ${
                              showThreads ? styles.threadsActive : ""
                            }`}
                            onClick={() => setShowThreads(!showThreads)}
                          />
                        )}
                        {msg?.Thread?.Count > 1 &&
                          msg?.Thread?.UnreadCount > 0 && (
                            <Tooltip title={t("chat.unreadInThread")}>
                              <div className={styles.colorBar}></div>
                            </Tooltip>
                          )}
                      </div>
                    </>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.titleWrapper}>
              {msg.Kind !== ItemKind.ContactMessage && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      msg.Kind === ItemKind.CourtPortalDeliverance ||
                        msg.Kind === ItemKind.CourtPortalDocument
                        ? (msg.Case?.Signature ?? msg.S1 ?? "") +
                            (msg.S1 && msg.Case?.Signature
                              ? " | " + (msg.Case.Client ?? "")
                              : "") +
                            (!msg.S1 && msg.Case?.Signature
                              ? " | " + (msg.S2 ?? "")
                              : "")
                        : cleanEmailSubject(msg.S1 ?? ""),
                    ),
                  }}
                  className={styles.title}
                ></div>
              )}
              {msg.Kind === ItemKind.ContactMessage && (
                <div className={styles.title}>
                  {msg.S1 && msg.S1 !== "" ? msg.S1 : t("packages.encrypted")}
                </div>
              )}
              {!isMobile && (
                <div className={`${styles.icons}`} ref={iconsRef}>
                  <IconsState
                    onDownload={onDownload}
                    onPreview={onPreview}
                    keys={keys}
                    msg={msg}
                    inDeskView={inDeskView}
                    setShowEditStatus={(show) => {
                      if (show) {
                        setStatusPopperRef(iconsRef);
                      }

                      setShowEditStatus(show);
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.tagRow}>
              <div className={styles.tags} ref={tagsRef}>
                {findedTags.map((tag) => (
                  <Chip
                    key={`${msg.Id}_${tag.Id}`}
                    onClick={() => {
                      setTagPopperRef(tagsRef);
                      setShowEditTags(true);
                    }}
                    small
                    outlined={false}
                    element={<>{tag.Name}</>}
                  />
                ))}
              </div>

              {!msg.ReceiveDate &&
                isWaitingToReceive &&
                linkStatus === "incoming" && (
                  <div className={styles.status}>
                    <PackageBar
                      id={msg.Id}
                      date={msg.SendDate}
                      expirationDate={msg.ExpirationTime}
                      totalDays={msg.ExpirationDays}
                      expired={msg.Expired}
                    />
                  </div>
                )}

              {refWrapper.current && (
                <OptionsCard
                  toggle={() => {
                    setShowOptions(false);
                    setIsRightClicked(false);
                  }}
                  dropdown={isRightClicked}
                  isRightClicked={isRightClicked}
                  binds
                  selectedElements={selectedElements ?? []}
                  setSelectedElements={setSelectedElements}
                  showOptions={
                    (showOptions && (selectedElements?.length ?? 0) <= 1) ||
                    isFirst
                  }
                  pack={msg}
                  refetch={() => {}}
                  remove={remove}
                  refWrapper={refWrapper}
                  onClickAttachIcon={setShowAttach}
                  scrollWrapper={scrollWrapper}
                  showEditStatus={showEditStatus}
                  setShowEditStatus={(show) => {
                    setShowEditStatus(show);
                    setStatusPopperRef(null);
                  }}
                  showEditTags={showEditTags}
                  setShowEditTags={(show) => {
                    setShowEditTags(show);
                    setTagPopperRef(null);
                  }}
                  showShare={showShare}
                  setShowShare={(show) => {
                    setShowShare(show);
                    setSharePopperRef(null);
                  }}
                />
              )}
            </div>
          </div>
          {msg.Thread && !isThread && showThreads && (
            <ThreadList
              isSelectable={isSelectable}
              selectMultiple={selectMultiple}
              selectedElements={selectedElements}
              setSelectedElements={setSelectedElements}
              callClose={callClose}
              toggle={() => {
                setShowThreads(false);
                setCallClose(false);
              }}
              onPreview={onPreview}
              pack={msg}
              isIncoming={isIncoming}
            />
          )}
        </div>

        <div
          className={`${styles.separator} ${
            !!findedTags.length && styles.sep
          } ${isMouse && styles.hideSep} ${isThread && styles.threadSeparator}`}
        ></div>
      </div>
      <div className={`${styles.separator}`}></div>

      {showEditStatus && (
        <Popper
          anchorEl={statusPopperRef?.current ?? contentRef?.current}
          open={showEditStatus}
          setOpen={setShowEditStatus}
        >
          <StatusModal
            item={msg}
            toggle={setShowEditStatus}
            selectedStatus={status}
            afterUpdate={handleStatusUpdate}
          />
        </Popper>
      )}
      {showEditTags && (
        <Popper
          anchorEl={tagPopperRef?.current ?? contentRef?.current}
          open={showEditTags}
          setOpen={setShowEditTags}
        >
          <ChooseTagNew
            toTag={msg}
            toggle={setShowEditTags}
            updateTags={handleUpdateTags}
            type={TagType.Incoming}
          />
        </Popper>
      )}
      {showShare && (
        <Popper
          anchorEl={sharePopperRef?.current ?? contentRef?.current}
          open={showShare}
          setOpen={setShowShare}
        >
          <PackageEditSharesModal
            toggle={() => setShowShare(false)}
            item={msg}
            refetch={() => {}}
          />
        </Popper>
      )}

      <Popper
        open={showDateMetric}
        anchorEl={dateRef?.current}
        setOpen={setShowDateMetic}
      >
        <MetricDatesModal toggle={setShowDateMetic} pack={msg} />
      </Popper>
    </div>
  );
};

export default MessageCard;
